/**
 * Theme A (theory) curriculum map for the study hub.
 *
 * Three-level hierarchy: topic → subtopic → statement.
 * `scope` values are syllabus prefixes accepted by the content endpoint
 * (e.g. 'A1' for the whole topic, 'A1.1' for a subtopic, 'A1.1.1' for one statement).
 *
 * `hlOnly: true` on a subtopic means the entire subtopic is HL.
 * `hlOnly: true` on a statement means only that statement is HL (within an otherwise SL subtopic).
 */

export const THEME_A = [
  {
    id: 'A1',
    title: 'A1 Computer fundamentals',
    icon: '🖥️',
    subtopics: [
      {
        scope: 'A1.1',
        title: 'A1.1 Computer hardware and operation',
        statements: [
          { scope: 'A1.1.1', title: 'Describe the functions and interactions of the main CPU components' },
          { scope: 'A1.1.2', title: 'Describe the role of a GPU' },
          { scope: 'A1.1.3', title: 'Explain the differences between the CPU and the GPU', hlOnly: true },
          { scope: 'A1.1.4', title: 'Explain the purposes of different types of primary memory' },
          { scope: 'A1.1.5', title: 'Describe the fetch, decode and execute cycle' },
          { scope: 'A1.1.6', title: 'Describe the process of pipelining in multi-core architectures', hlOnly: true },
          { scope: 'A1.1.7', title: 'Describe internal and external types of secondary memory storage' },
          { scope: 'A1.1.8', title: 'Describe the concept of compression' },
          { scope: 'A1.1.9', title: 'Describe the different types of services in cloud computing' },
        ],
      },
      {
        scope: 'A1.2',
        title: 'A1.2 Data representation and logic',
        statements: [
          { scope: 'A1.2.1', title: 'Describe the principal methods of representing data' },
          { scope: 'A1.2.2', title: 'Explain how binary is used to store data' },
          { scope: 'A1.2.3', title: 'Describe the purpose and use of logic gates' },
          { scope: 'A1.2.4', title: 'Construct and analyse truth tables' },
          { scope: 'A1.2.5', title: 'Construct logic diagrams' },
        ],
      },
      {
        scope: 'A1.3',
        title: 'A1.3 Operating systems and control systems',
        statements: [
          { scope: 'A1.3.1', title: 'Describe the role of operating systems' },
          { scope: 'A1.3.2', title: 'Describe the functions of an operating system' },
          { scope: 'A1.3.3', title: 'Compare different approaches to scheduling' },
          { scope: 'A1.3.4', title: 'Evaluate the use of polling and interrupt handling' },
          { scope: 'A1.3.5', title: 'Explain the OS role in multitasking and resource allocation', hlOnly: true },
          { scope: 'A1.3.6', title: 'Describe the use of control system components', hlOnly: true },
          { scope: 'A1.3.7', title: 'Explain the use of control systems in real-world applications', hlOnly: true },
        ],
      },
      {
        scope: 'A1.4',
        title: 'A1.4 Translation',
        hlOnly: true,
        statements: [
          { scope: 'A1.4.1', title: 'Evaluate the translation processes of interpreters and compilers', hlOnly: true },
        ],
      },
    ],
  },
  {
    id: 'A2',
    title: 'A2 Networks',
    icon: '🌐',
    subtopics: [
      {
        scope: 'A2.1',
        title: 'A2.1 Network fundamentals',
        statements: [
          { scope: 'A2.1.1', title: 'Describe the purpose and characteristics of networks' },
          { scope: 'A2.1.2', title: 'Describe the purpose, benefits and limitations of digital infrastructures' },
          { scope: 'A2.1.3', title: 'Describe the function of network devices' },
          { scope: 'A2.1.4', title: 'Describe network protocols used for transport and application layers' },
          { scope: 'A2.1.5', title: 'Describe the function of the TCP/IP model', hlOnly: true },
        ],
      },
      {
        scope: 'A2.2',
        title: 'A2.2 Network architecture',
        statements: [
          { scope: 'A2.2.1', title: 'Describe functions and applications of network topologies' },
          { scope: 'A2.2.2', title: 'Describe the function of servers', hlOnly: true },
          { scope: 'A2.2.3', title: 'Compare and contrast networking models' },
          { scope: 'A2.2.4', title: 'Explain concepts and applications of network segmentation' },
        ],
      },
      {
        scope: 'A2.3',
        title: 'A2.3 Data transmissions',
        statements: [
          { scope: 'A2.3.1', title: 'Describe different types of IP addressing' },
          { scope: 'A2.3.2', title: 'Compare types of media for data transmission' },
          { scope: 'A2.3.3', title: 'Explain how packet switching is used to send data' },
          { scope: 'A2.3.4', title: 'Explain how static and dynamic routing move data on LANs', hlOnly: true },
        ],
      },
      {
        scope: 'A2.4',
        title: 'A2.4 Network security',
        statements: [
          { scope: 'A2.4.1', title: 'Discuss the effectiveness of firewalls at protecting a network' },
          { scope: 'A2.4.2', title: 'Describe common network vulnerabilities', hlOnly: true },
          { scope: 'A2.4.3', title: 'Describe common network countermeasures', hlOnly: true },
          { scope: 'A2.4.4', title: 'Describe the process of encryption and digital certificates' },
        ],
      },
    ],
  },
  {
    id: 'A3',
    title: 'A3 Databases',
    icon: '🗄️',
    subtopics: [
      {
        scope: 'A3.1',
        title: 'A3.1 Database fundamentals',
        statements: [
          { scope: 'A3.1.1', title: 'Explain the features, benefits and limitations of a relational database' },
        ],
      },
      {
        scope: 'A3.2',
        title: 'A3.2 Database design',
        statements: [
          { scope: 'A3.2.1', title: 'Describe database schemas' },
          { scope: 'A3.2.2', title: 'Construct entity-relationship diagrams (ERDs)' },
          { scope: 'A3.2.3', title: 'Outline the different data types used in relational databases' },
          { scope: 'A3.2.4', title: 'Construct tables for relational databases' },
          { scope: 'A3.2.5', title: 'Explain the difference between normal forms' },
          { scope: 'A3.2.6', title: 'Construct a database normalised to 3NF for real-world scenarios' },
          { scope: 'A3.2.7', title: 'Evaluate the need for denormalising databases' },
        ],
      },
      {
        scope: 'A3.3',
        title: 'A3.3 Database programming',
        statements: [
          { scope: 'A3.3.1', title: 'Compare data definition language (DDL) and data manipulation language (DML)' },
          { scope: 'A3.3.2', title: 'Construct SQL queries using SELECT, FROM, WHERE, ORDER BY, GROUP BY, JOIN' },
          { scope: 'A3.3.3', title: 'Construct SQL statements that modify data (INSERT, UPDATE, DELETE)' },
          { scope: 'A3.3.4', title: 'Construct SQL queries using aggregate functions', hlOnly: true },
          { scope: 'A3.3.5', title: 'Describe database views', hlOnly: true },
          { scope: 'A3.3.6', title: 'Explain how transactions and ACID properties ensure data integrity', hlOnly: true },
        ],
      },
      {
        scope: 'A3.4',
        title: 'A3.4 Alternative databases',
        hlOnly: true,
        statements: [
          { scope: 'A3.4.1', title: 'Outline the different types of databases as approaches to storing data', hlOnly: true },
          { scope: 'A3.4.2', title: 'Explain the primary objectives of data warehouses in data management', hlOnly: true },
          { scope: 'A3.4.3', title: 'Explain the role of OLAP and data mining in business intelligence', hlOnly: true },
          { scope: 'A3.4.4', title: 'Describe the features of distributed databases', hlOnly: true },
        ],
      },
    ],
  },
  {
    id: 'A4',
    title: 'A4 Machine learning',
    icon: '🤖',
    subtopics: [
      {
        scope: 'A4.1',
        title: 'A4.1 ML fundamentals',
        statements: [
          { scope: 'A4.1.1', title: 'Describe the types of machine learning and their real-world applications' },
          { scope: 'A4.1.2', title: 'Describe the hardware requirements for various ML scenarios' },
        ],
      },
      {
        scope: 'A4.2',
        title: 'A4.2 Data preprocessing',
        hlOnly: true,
        statements: [
          { scope: 'A4.2.1', title: 'Describe the significance of data cleaning', hlOnly: true },
          { scope: 'A4.2.2', title: 'Describe the role of feature selection', hlOnly: true },
          { scope: 'A4.2.3', title: 'Describe the importance of dimensionality reduction', hlOnly: true },
        ],
      },
      {
        scope: 'A4.3',
        title: 'A4.3 ML approaches',
        hlOnly: true,
        statements: [
          { scope: 'A4.3.1',  title: 'Explain how linear regression predicts continuous outcomes', hlOnly: true },
          { scope: 'A4.3.2',  title: 'Explain classification techniques (K-NN, decision trees)', hlOnly: true },
          { scope: 'A4.3.3',  title: 'Explain hyperparameter tuning and evaluation metrics', hlOnly: true },
          { scope: 'A4.3.4',  title: 'Describe clustering in unsupervised learning', hlOnly: true },
          { scope: 'A4.3.5',  title: 'Describe association-rule learning', hlOnly: true },
          { scope: 'A4.3.6',  title: 'Describe reinforcement learning (agent, environment, state, reward)', hlOnly: true },
          { scope: 'A4.3.7',  title: 'Describe genetic algorithms (population, fitness, selection, mutation)', hlOnly: true },
          { scope: 'A4.3.8',  title: 'Outline artificial neural networks (ANNs) and the perceptron model', hlOnly: true },
          { scope: 'A4.3.9',  title: 'Describe convolutional neural networks (CNNs) and spatial hierarchies', hlOnly: true },
          { scope: 'A4.3.10', title: 'Explain model selection and comparison', hlOnly: true },
        ],
      },
      {
        scope: 'A4.4',
        title: 'A4.4 Ethical considerations',
        statements: [
          { scope: 'A4.4.1', title: 'Discuss the ethical implications of machine learning in real-world scenarios' },
          { scope: 'A4.4.2', title: 'Discuss ethical aspects of increasing computer system integration' },
        ],
      },
    ],
  },
];
